import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Divider,
    FormControlLabel,
    Checkbox,
    Grid,
    Paper,
    TextField,
    Typography,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { LinearProgress } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import BusinessIcon from '@mui/icons-material/Business';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import { usePurchaseContractsController } from "./context/purchase-contracts-controller";
import { usePurchaseContractsState } from "./context/purchase-contracts-context";
import { IListPurchaseContractsRequest } from "../../model/base/request/list-purchase-contracts-request";
import PageHeader from "../components/shared/page-header";
import { useNavigate } from "../../hooks/useNavigate";
import FilterPanel, { ResultsPanel } from '../components/shared/filter-panel';
import EmptyState, { hasActiveFilters } from '../components/shared/empty-state';
import ListSkeleton from '../components/shared/list-skeleton';

export function PurchaseContractsComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData } = usePurchaseContractsController();
    const { purchaseContracts } = usePurchaseContractsState();
    const [paramsRequest, setParamsRequest] = useState<IListPurchaseContractsRequest>({ page: 0, pageSize: 10 } as IListPurchaseContractsRequest);
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequest);
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
    }, [paramsRequest]);

    const handleClearFilters = () => {
        setParamsRequest({ page: 0, pageSize: 10 } as IListPurchaseContractsRequest);
        setStartDate(null);
        setEndDate(null);
    };

    const handleStartDateChange = (newValue: Dayjs | null) => {
        setStartDate(newValue);
        setParamsRequest({
            ...paramsRequest,
            page: 0,
            startDate: newValue ? newValue.format('YYYY-MM-DD') : undefined
        });
    };

    const handleEndDateChange = (newValue: Dayjs | null) => {
        setEndDate(newValue);
        setParamsRequest({
            ...paramsRequest,
            page: 0,
            endDate: newValue ? newValue.format('YYYY-MM-DD') : undefined
        });
    };

    const formatCNPJ = (cnpj: string) => {
        if (!cnpj) return '-';
        const cleaned = cnpj.replace(/\D/g, '');
        return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const handleViewDetails = (row: any) => {
        if (row?.externalUrl) {
            window.open(row.externalUrl, '_blank', 'noopener,noreferrer');
        } else {
            navigate(`/contratos-aquisicoes/${row.id}`);
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'contractedCompanyName',
            headerName: 'Empresa',
            flex: 2,
        },
        {
            field: 'contractedCompanyDocument',
            headerName: 'CNPJ',
            flex: 1,
            renderCell: (params) => formatCNPJ(params.value),
        },
        {
            field: 'contractDeadline',
            headerName: 'Prazo',
            flex: 1,
            renderCell: (params) => {
                return params.value ? dayjs(params.value).format('DD/MM/YYYY') : '-';
            },
        },
        {
            field: 'amountPaid',
            headerName: 'Valor Pago',
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => formatCurrency(params.value),
        },
        {
            field: 'balance',
            headerName: 'Saldo',
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => formatCurrency(params.value),
        },
        {
            field: 'isPncp',
            headerName: 'PNCP',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                params.value ? (
                    <Chip
                        icon={<VerifiedIcon />}
                        label="PNCP"
                        color="success"
                        size="small"
                    />
                ) : null
            ),
        },
        {
            field: 'isCovid19',
            headerName: 'COVID-19',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                params.value ? (
                    <Chip
                        label="COVID-19"
                        color="warning"
                        size="small"
                    />
                ) : null
            ),
        },
        {
            field: 'actions',
            headerName: 'Ações',
            width: 180,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="text"
                    size="small"
                    endIcon={params.row.externalUrl ? <OpenInNewIcon /> : <ArrowForwardIcon />}
                    onClick={() => handleViewDetails(params.row)}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500
                    }}
                >
                    {params.row.externalUrl ? 'Ver externo' : 'Ver detalhes'}
                </Button>
            ),
        },
    ];

    return (
        <Box>
            <PageHeader
                title="Contratos e Aquisições"
                subtitle="Consulte todos os contratos e aquisições realizados pelo município, promovendo transparência e controle sobre os recursos públicos."
                breadcrumbs={[{ label: 'Contratos e Aquisições' }]}
            />

            {initialLoading ? (
                <ListSkeleton />
            ) : (
                <React.Fragment>
                    <FilterPanel>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6} md={3.5}>
                                    <TextField
                                        fullWidth
                                        label="Buscar"
                                        placeholder="Digite para buscar"
                                        value={paramsRequest.term || ''}
                                        onChange={(e) => {
                                            setParamsRequest({ ...paramsRequest, page: 0, term: e.target.value });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.5}>
                                    <DatePicker
                                        label="Data de"
                                        value={startDate}
                                        onChange={handleStartDateChange}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.5}>
                                    <DatePicker
                                        label="Data até"
                                        value={endDate}
                                        onChange={handleEndDateChange}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={1.5}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={paramsRequest.isCovid19 || false}
                                                onChange={(e) => {
                                                    setParamsRequest({
                                                        ...paramsRequest,
                                                        page: 0,
                                                        isCovid19: e.target.checked
                                                    });
                                                }}
                                            />
                                        }
                                        label="COVID-19"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<ClearIcon />}
                                        onClick={handleClearFilters}
                                        fullWidth
                                        sx={{ height: '56px' }}
                                    >
                                        Limpar Filtros
                                    </Button>
                                </Grid>
                            </Grid>
                        </LocalizationProvider>
                    </FilterPanel>

                    <ResultsPanel>
                        {loadMoreData ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                                <LinearProgress sx={{ width: '50%' }} />
                            </Box>
                        ) : purchaseContracts?.items?.length === 0 ? (
                            <EmptyState title="Nenhum contrato encontrado" filtered={hasActiveFilters(paramsRequest)} onClearFilters={handleClearFilters} />
                        ) : isMobile ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {(purchaseContracts?.items || []).map((contract: any) => (
                                    <Paper key={contract.id} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                        <Box
                                            sx={{
                                                backgroundColor: 'brand.surface',
                                                borderBottom: 1,
                                                borderColor: 'divider',
                                                px: 2,
                                                py: 1.25,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <BusinessIcon sx={{ fontSize: 22, color: 'brand.text' }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', flex: 1 }}>
                                                {contract.contractedCompanyName || '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ px: 2, py: 1.5 }}>
                                            <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                                                {contract.isPncp && (
                                                    <Chip icon={<VerifiedIcon />} label="PNCP" color="success" size="small" />
                                                )}
                                                {contract.isCovid19 && (
                                                    <Chip label="COVID-19" color="warning" size="small" />
                                                )}
                                            </Box>
                                            <Typography variant="overline" component="p" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                CNPJ
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                {formatCNPJ(contract.contractedCompanyDocument)}
                                            </Typography>
                                            <Typography variant="overline" component="p" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                Prazo
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                {contract.contractDeadline ? dayjs(contract.contractDeadline).format('DD/MM/YYYY') : '-'}
                                            </Typography>
                                            <Typography variant="overline" component="p" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                Valor Pago
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                {formatCurrency(contract.amountPaid)}
                                            </Typography>
                                            <Typography variant="overline" component="p" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                Saldo
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatCurrency(contract.balance)}
                                            </Typography>
                                        </Box>
                                        <Divider />
                                        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="text"
                                                size="small"
                                                endIcon={contract.externalUrl ? <OpenInNewIcon /> : <ArrowForwardIcon />}
                                                onClick={() => handleViewDetails(contract)}
                                                sx={{ textTransform: 'none', fontWeight: 500 }}
                                            >
                                                {contract.externalUrl ? 'Ver externo' : 'Ver detalhes'}
                                            </Button>
                                        </Box>
                                    </Paper>
                                ))}
                            </Box>
                        ) : (
                            <DataGrid
                                key={JSON.stringify(paramsRequest)}
                                rows={purchaseContracts?.items || []}
                                rowCount={purchaseContracts?.totalRecords || 0}
                                columns={columns}
                                getRowId={(row) => row.id}
                                hideFooterSelectedRowCount
                                paginationModel={{ page: paramsRequest.page, pageSize: paramsRequest.pageSize } as GridPaginationModel}
                                onPaginationModelChange={(e) => { setParamsRequest({ ...paramsRequest, page: e.page, pageSize: e.pageSize }) }}
                                pageSizeOptions={[5]}
                                paginationMode="server"
                                slots={{
                                    loadingOverlay: LinearProgress,
                                }}
                                loading={loadMoreData}
                                localeText={{
                                    MuiTablePagination: {
                                        labelDisplayedRows: ({ from, to, count }) =>
                                            `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`,
                                        labelRowsPerPage: 'Linhas por página:',
                                    },
                                    noRowsLabel: 'Nenhum registro encontrado',
                                    noResultsOverlayLabel: 'Nenhum resultado encontrado',
                                }}
                            />
                        )}
                    </ResultsPanel>
                </React.Fragment>
            )}
        </Box>
    );
}
